// UpdateJobs.tsx
import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { PuffLoader } from "react-spinners";
import moment from "moment";
import { ContentEditableEvent } from "react-simple-wysiwyg";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/store";
import {
  addJob,
  getJobById,
  getJobQuestionAndAnswer,
  getJobTypes,
  getQuestionCategory,
  getQuestions,
  getSalaryTypes,
  updateJob,
} from "../../features/jobSlice";

import JobFormFields from "../../Components/UpdateJob/JobFormFields";
import QuestionSelector from "../../Components/UpdateJob/QuestionSelector";
import { UpdateJobData } from "../../types";
import AuthLayout from "../../Components/Layouts/AuthLayout";

const UpdateJobs: React.FC = () => {
  //state to divide the page into two
  const [currentPart, setCurrentPart] = useState(1);
  const [loading, setLoading] = useState(false);
  const [executed, setExecuted] = useState(false);
  const [textBoxes1, setTextBoxes1] = useState<string[]>([""]);
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [availableQuestions, setAvailableQuestions] = useState<
    Record<number, { data: any[] }>
  >({});
  const [employeeQuestions, setEmployeeQuestions] = useState<string[]>([]);
  const [textBoxes, setTextBoxes] = useState<string[]>([""]); // Initial state with one text box
  const [formData, setFormData] = useState<UpdateJobData>({
    jobTitle: "",
    jobLocation: "",
    department: "",
    jobType: "",
    jobDescription: "",
    otherInfo: "",
    salaryType: "",
    minSalary: 0,
    maxSalary: 0,
    perHourRate: 0,
    contactEmail: "",
    contactNumberStatus: "",
    contactNumber: "",
    negotiable: "",
    jobRole: "",
    numberOfPositions: 0,
    companyName: "",
    industry: "",
    appUrl: "",
    hiringManager: "",
    openDate: new Date(),
    closeDate: new Date(),
    jobStatus: "",
    assignedTeam: "",
    jobCategory: "",
    jobQuestions: null,
    employeeQuestions: null,
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const validJobId = id ? Number(id) : 0;

  const { singleJob, jobTypes, salaryTypes, questionCategory } = useAppSelector(
    (state) => state.job
  );

  // Fetch job and related data on mount
  useEffect(() => {
    dispatch(getQuestionCategory());
    dispatch(getJobTypes());
    dispatch(getSalaryTypes());

    if (!validJobId) return navigate("/error");

    setLoading(true);
    dispatch(getJobById(validJobId)).finally(() => setLoading(false));
  }, [dispatch, validJobId, navigate, executed]);

  useEffect(() => {
    if (singleJob) {
      // Set main form data
      setFormData({
        ...formData,
        ...singleJob,
        jobQuestions: singleJob.job_question || null,
        employeeQuestions: singleJob.employee_questions || null,
      });

      // If job_question is a valid array
      if (Array.isArray(singleJob.job_question)) {
        setEmployeeQuestions(
          singleJob.employee_questions.map((q: any) => q.questions || "")
        );

        const selectedIds: number[] = singleJob.job_question
          .map((q: any) => q?.pre_questions?.id)
          .filter((id: number | undefined): id is number => !!id);
        setSelectedQuestions(selectedIds);

        const categoryIds: number[] = singleJob.job_question
          .map((q: any) => q?.pre_questions?.category_id)
          .filter((id: number | undefined): id is number => !!id);

        // Remove duplicates
        const uniqueCategoryIds = Array.from(new Set(categoryIds));
        setTextBoxes1(uniqueCategoryIds.map(String)); // for UI rendering

        // Fetch all questions for each category
        const categoryPromises = uniqueCategoryIds.map((categoryId) =>
          dispatch(getQuestions(String(categoryId)))
            .unwrap()
            .then((response) => ({
              categoryId,
              data: response.data.map((q: any) => ({
                jobQuestion: q.jobQuestion,
                id: q.id,
              })),
            }))
        );

        Promise.all(categoryPromises)
          .then((results) => {
            const questionMap: Record<number, { data: any[] }> = {};
            results.forEach(({ categoryId, data }) => {
              questionMap[categoryId] = { data };
            });
            setAvailableQuestions(questionMap);
          })
          .catch(console.error);
      }
    }
  }, [singleJob]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditorChange = (
    editorName: keyof UpdateJobData,
    event: ContentEditableEvent
  ) => {
    setFormData((prev) => ({ ...prev, [editorName]: event.target.value }));
  };

  const formatDate = (date: Date | undefined): string => {
    return date ? moment(date).format("YYYY-MM-DD") : "";
  };

  const handleChangeCat = async (
    e: React.ChangeEvent<HTMLSelectElement>,
    index: number
  ) => {
    const categoryId = parseInt(e.target.value);

    const updatedCategories = [...textBoxes1];
    updatedCategories[index] = e.target.value; // update UI
    setTextBoxes1(updatedCategories);

    try {
      const response = await dispatch(
        getQuestions(String(categoryId))
      ).unwrap();

      setAvailableQuestions((prev) => ({
        ...prev,
        [categoryId]: {
          data: response.data.map((q: any) => ({
            jobQuestion: q.jobQuestion,
            id: q.id,
          })),
        },
      }));
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };

  const handleQuestionSelect = (questionId: number) => {
    setSelectedQuestions((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Update the addTextBox function
  const addTextBox = () => {
    setEmployeeQuestions((prev) => [...prev, ""]);
  };

  // Update the removeTextBox function
  const removeTextBox = (index: number) => {
    setEmployeeQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleChangeJob = (index: number, value: string) => {
    setEmployeeQuestions((prev) =>
      prev.map((q, i) => (i === index ? value : q))
    );
  };

  const addEmployeeQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      employeeQuestions: [
        ...(prev.employeeQuestions || []),
        {
          id: 0,
          questions: "",
          job_id: validJobId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ],
    }));
  };

  // Update the handleUpdateJob function
  const handleUpdateJob = (status: string) => {
    const data = {
      ...formData,
      jobStatus: status,
      jobId: validJobId,
      jobQuestionIds: selectedQuestions,
      jobQuestions: employeeQuestions.filter((q) => q.trim() !== ""),
    };

    dispatch(addJob(data))
      .unwrap()
      .then((response: any) => {
        navigate("/jobs");
        toast.success(response.message);
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5 mt-5">
        <PuffLoader color="#073367" size={150} />
      </div>
    );
  }

  return (
    <div>
      <AuthLayout>
        <span
          style={{ cursor: "pointer" }}
          onClick={() => navigate(-1)}
          className="text-decoration-none fw-bold text-primary"
        >
          <i className="bi bi-arrow-left border-primary"></i> Back
        </span>
        <div className="bg-white p-3 mt-5">
          <Row>
            <h4 className="fw-bold">Duplicate Job</h4>
            <hr className="w-100 border-blend" />

            <Col md={12}>
              {currentPart === 1 && (
                <>
                  <JobFormFields
                    formData={formData}
                    jobTypes={jobTypes}
                    salaryTypes={salaryTypes}
                    handleChange={handleChange}
                    formatDate={formatDate}
                    handleEditorChange={handleEditorChange}
                  />{" "}
                </>
              )}
              <QuestionSelector
                questionCategories={questionCategory?.data || []}
                availableQuestions={availableQuestions}
                selectedQuestions={selectedQuestions}
                handleChangeCat={handleChangeCat}
                handleQuestionSelect={handleQuestionSelect}
                textBoxes1={textBoxes1}
                setTextBoxes1={setTextBoxes1}
                addTextBox={addTextBox}
                removeTextBox={removeTextBox}
                employeeQuestions={employeeQuestions}
                onEmployeeQuestionChange={handleChangeJob}
                onAddEmployeeQuestion={addTextBox}
                onRemoveEmployeeQuestion={removeTextBox}
                textBoxes={textBoxes}
                setTextBoxes={setTextBoxes}
                currentPart={currentPart}
                setCurrentPart={setCurrentPart}
              />
              {currentPart === 1 && (
                <div className="d-flex justify-content-end">
                  <Button
                    className="btn btn-primary float-end w-10"
                    onClick={() => setCurrentPart(2)}
                  >
                    Next
                  </Button>
                </div>
              )}
              {currentPart === 2 && (
                <Col md={12} className="mt-5">
                  <div className="d-flex">
                    <Button
                      className="btn btn-secondary float-end w-10 text-white me-2"
                      onClick={() => setCurrentPart(1)}
                    >
                      Previous
                    </Button>
                    <Button
                      className="btn btn-secondary text-white btn-sm me-2 px-4"
                      onClick={() => handleUpdateJob("Saved")}
                    >
                      Save
                    </Button>
                    <Button
                      className="btn btn-green text-white btn-sm me-2 px-4"
                      onClick={() => handleUpdateJob("Published")}
                    >
                      Publish Job
                    </Button>
                    <Button
                      className="btn btn-secondary text-white btn-sm me-2 px-4"
                      onClick={() => handleUpdateJob("Published")}
                    >
                      Publish To Job Boards
                    </Button>
                  </div>
                </Col>
              )}
            </Col>
          </Row>
        </div>
      </AuthLayout>
    </div>
  );
};

export default UpdateJobs;
